import { z } from "zod";
import type { Archive } from "../../domain/types";
import { callLLMWithRoutes } from "../../services/llm";
import {
  resolveFeatureRoutes,
  type FeatureRoutingSettings,
} from "../../services/featureRoutes";
import { loadEvolutionPreset } from "./service";

const responseSchema = z.object({ biography: z.string().min(200) });

export function needsBiographyCompression(recentEvents: string[]) {
  return recentEvents.join("\n").length > 1000;
}
function parseResponse(raw: string) {
  const fenced = raw.match(/```(?:json)?\s*([\s\S]*?)```/i)?.[1] ?? raw;
  return responseSchema.parse(JSON.parse(fenced.trim())).biography.trim();
}

export async function settleBiographyCompression(
  archive: Archive,
  settings: FeatureRoutingSettings,
): Promise<Archive> {
  const targets = Object.values(archive.characters).filter((character) =>
    needsBiographyCompression(character.bio.recentEvents),
  );
  if (!targets.length) return archive;
  let template = "";
  try {
    template = (await loadEvolutionPreset()).bioCompressionTemplate;
  } catch {
    return archive;
  }
  const routeFeature = settings.evolutionRoutesSplit === false ? "evolution" : "evolution-bio";
  const routes = resolveFeatureRoutes(routeFeature, settings).map(
    (route) => ({
      ...route,
      id: `evolution-bio:${route.id ?? route.model}`,
      temperature: 0.2,
      retries: 1,
    }),
  );
  const results = await Promise.all(
    targets.map(async (character) => {
      try {
        const response = await callLLMWithRoutes(
          routes,
          [
            { role: "system", content: template },
            {
              role: "user",
              content: `只输出 JSON：{"biography":"压缩后的长期传记"}。\n\n【NPC】\n${JSON.stringify({ id: character.id, identity: character.identity, relations: character.relations, goals: { current: character.bio.currentMotive, short: character.bio.shortTermGoal, long: character.bio.longTermGoal } })}\n\n【既有长期传记】\n${character.bio.biography || "无"}\n\n【并入长期传记的旧事件】\n${character.bio.recentEvents.slice(0, -3).join("\n")}`,
            },
          ],
          { stream: false, responseFormat: "json" },
        );
        return {
          id: character.id,
          biography: parseResponse(response.text),
          recentEvents: character.bio.recentEvents.slice(-3),
        };
      } catch {
        return null;
      }
    }),
  );
  if (results.every((result) => result === null)) return archive;
  const next = structuredClone(archive);
  for (const result of results)
    if (result && next.characters[result.id]) {
      next.characters[result.id].bio.biography = result.biography;
      next.characters[result.id].bio.recentEvents = result.recentEvents;
    }
  return next;
}
